import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, forkJoin, switchMap, map } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';

import type { AutoFillMapping, Entry, Relation, RelationItem } from '../../features/admin/modules/modules.model';
import { ModulesStore } from '../../features/admin/modules/modules.store';
import { ModulesService } from '../../api/services/modules.service';
import { FormService } from '../../features/admin/modules/features/form-builder/form.service';
import { FormHelper } from '../../features/admin/modules/features/form-builder/form.util';
import { UtilityService } from './utility.service';
import { evaluateFiltersAgainstEntry } from './filter-evaluator.util';
import { debugLog } from '../utils/debug-logger';
import {
  evaluateMappingConditions,
  extractArrayIndicesFromField,
  resolveScopedKeyPath,
} from '../utils/mapping-condition-evaluator.util';
import {
  AutoFillCombinedDialogComponent,
  AutoFillPendingItem,
  CombinedAutoFillDialogData,
  RelationOptionWithItems,
  RelationSelectionGroup,
} from '../components/auto-fill-combined-dialog.component';
import { UserStore } from '../../core/services/user.store';
import {
  canReadFieldRight,
  canWriteFieldRight,
  effectiveFieldRightForKeyPath,
} from '../../features/admin/modules/field-rights.util';

export interface AutoFillResult {
  /** Clean keyPath without indices, e.g. "entry.general.users.users.user" */
  targetFieldKeyPath: string;
  /** Path for Angular form.get(), e.g. "users.0.users.0.user" */
  formControlPath: string;
  value: unknown;
  /** Array indices for nested repeat fields, e.g. [0, 0] */
  repeatIndex?: number[];
}

interface MappingProcessResult {
  directItems: AutoFillPendingItem[];
  relationChoice?: RelationSelectionGroup;
}

interface CollectedResults {
  directItems: AutoFillPendingItem[];
  relationChoices: RelationSelectionGroup[];
}

@Injectable({ providedIn: 'root' })
export class CascadingAutoFillService {
  readonly #modulesStore = inject(ModulesStore);
  readonly #modulesService = inject(ModulesService);
  readonly #formService = inject(FormService);
  readonly #dialog = inject(MatDialog);
  readonly #userStore = inject(UserStore);

  /**
   * Process cascading auto-fill for all mappings after an autocomplete selection.
   * Shows a single combined dialog for relation selection and/or overwrite confirmation.
   */
  processCascadingAutoFill(
    selectedValue: { id: string; selectedInput?: string },
    sourceModuleCode: string,
    mappings: AutoFillMapping[],
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    triggeringField?: FormlyFieldConfig
  ): Observable<AutoFillResult[]> {
    const fieldArrayIndices = extractArrayIndicesFromField(triggeringField);
    if (!mappings?.length || !selectedValue?.id) {
      return of([]);
    }

    const sourceModule = this.#modulesStore.getModuleByCode(sourceModuleCode);
    if (!sourceModule) {
      return of([]);
    }

    return this.#modulesService
      .getModuleEntry({
        moduleId: sourceModule.id,
        entryId: selectedValue.id,
      })
      .pipe(
        switchMap(fullSourceEntry => {
          const mappingResults$: Observable<MappingProcessResult>[] = mappings.map(mapping =>
            this.#processMapping(
              mapping,
              fullSourceEntry as Entry,
              sourceModuleCode,
              currentEntry,
              generatedForm,
              fieldArrayIndices
            )
          );

          if (mappingResults$.length === 0) {
            return of([]);
          }

          return forkJoin(mappingResults$).pipe(
            map(results => this.#collectResults(results)),
            switchMap(collected => this.#resolveAndConfirm(collected))
          );
        })
      );
  }

  /**
   * Process only the query-mappings among `mappings`, without a selected source entry.
   *
   * A query-mapping looks its value up in another module using the current entry as context, so
   * unlike a relation or copy mapping it needs no linked entry to read from. That makes it usable
   * by fields that fill siblings from their own value — the RDW licence-plate lookup resolves a
   * category profile and lets a query-mapping find the asset category carrying it.
   *
   * Mappings that do need a source entry are skipped rather than half-applied.
   *
   * `triggeringField` is the field that started the fill. It carries the repeat position, so pass
   * it whenever the caller sits inside a fieldArray — without it the mapping cannot tell which row
   * it is filling, and both its conditions and its target would resolve against the wrong item.
   */
  processQueryAutoFill(
    mappings: AutoFillMapping[],
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    triggeringField?: FormlyFieldConfig
  ): Observable<AutoFillResult[]> {
    const fieldArrayIndices = extractArrayIndicesFromField(triggeringField);

    // Same condition gate as #processMapping — a conditional mapping must not become
    // unconditional just because it is reached through the query route.
    const queryMappings = (mappings ?? []).filter(
      mapping =>
        !!mapping.queryModuleCode &&
        this.#conditionsAreReadable(mapping, generatedForm) &&
        evaluateMappingConditions(
          mapping.conditions,
          currentEntry,
          (c, v) => this.#formService.checkCondition(c, v),
          fieldArrayIndices
        )
    );
    if (!queryMappings.length) {
      return of([]);
    }

    return forkJoin(
      queryMappings.map(mapping => this.#processQueryMapping(mapping, currentEntry, generatedForm, fieldArrayIndices))
    ).pipe(
      map(results => this.#collectResults(results)),
      switchMap(collected => this.#resolveAndConfirm(collected))
    );
  }

  #processMapping(
    mapping: AutoFillMapping,
    fullSourceEntry: Entry,
    sourceModuleCode: string,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    fieldArrayIndices: number[] = []
  ): Observable<MappingProcessResult> {
    // Skip when the mapping has conditions that don't hold against the current entry.
    // No conditions = always apply.
    if (
      !this.#conditionsAreReadable(mapping, generatedForm) ||
      !evaluateMappingConditions(
        mapping.conditions,
        currentEntry,
        (c, v) => this.#formService.checkCondition(c, v),
        fieldArrayIndices
      )
    ) {
      return of({ directItems: [] });
    }

    if (mapping.queryModuleCode) {
      return this.#processQueryMapping(mapping, currentEntry, generatedForm, fieldArrayIndices);
    }

    if (!this.#canReadModuleField(fullSourceEntry, mapping.sourceFieldPath, sourceModuleCode)) {
      return of({ directItems: [] });
    }

    if (mapping.sourceFieldPath === 'entry.relations' && mapping.sourceRelationModuleCode) {
      return this.#processMappingFromRelations(mapping, fullSourceEntry, currentEntry, generatedForm);
    }

    const linkedValue = UtilityService.keyPathToValue(fullSourceEntry, mapping.sourceFieldPath);
    if (linkedValue == null) {
      return of({ directItems: [] });
    }

    const linkedId = typeof linkedValue === 'object' && linkedValue?.id ? linkedValue.id : null;
    if (!linkedId) {
      return of({ directItems: [] });
    }

    const targetField = this.#resolveTargetField(generatedForm, mapping.targetFieldKey);
    if (!targetField || !this.#canWriteTargetField(targetField, generatedForm)) {
      return of({ directItems: [] });
    }

    const linkedModuleCode = targetField.props?.optionsArray?.[0]?.moduleCode;
    if (!linkedModuleCode) {
      return of({ directItems: [] });
    }

    const linkedModule = this.#modulesStore.getModuleByCode(linkedModuleCode);
    if (!linkedModule) {
      return of({ directItems: [] });
    }

    if (!this.#canReadLinkedDisplayField(targetField, linkedModuleCode)) {
      return of({ directItems: [] });
    }

    if (!this.#filtersAreReadable(targetField, linkedModuleCode, generatedForm)) {
      return of({ directItems: [] });
    }

    const leafKey = this.#extractLeafKey(mapping.targetFieldKey);

    return this.#modulesService
      .getModuleEntry({
        moduleId: linkedModule.id,
        entryId: linkedId,
      })
      .pipe(
        map(fullLinkedEntry => {
          const linkedEntry = fullLinkedEntry as Entry;
          const results: AutoFillPendingItem[] = [];

          const isInArray = this.#isFieldInRepeatArray(generatedForm, leafKey);

          if (isInArray) {
            this.#processRepeatField(
              leafKey,
              targetField,
              linkedEntry,
              linkedModuleCode,
              currentEntry,
              generatedForm,
              results
            );
          } else {
            this.#processSimpleField(targetField, linkedEntry, linkedModuleCode, currentEntry, generatedForm, results);
          }

          return { directItems: results } as MappingProcessResult;
        })
      );
  }

  /**
   * Process relation-based mapping. When multiple relations match,
   * pre-calculates changes for ALL options so they can be presented
   * in a single combined dialog.
   */
  #processMappingFromRelations(
    mapping: AutoFillMapping,
    fullSourceEntry: Entry,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[]
  ): Observable<MappingProcessResult> {
    const relations = fullSourceEntry.relations;
    if (!relations?.length) return of({ directItems: [] });

    const relationModule = this.#modulesStore.getModuleByCode(mapping.sourceRelationModuleCode!);
    if (!relationModule) return of({ directItems: [] });

    const targetField = this.#resolveTargetField(generatedForm, mapping.targetFieldKey);
    if (
      !targetField ||
      !this.#canWriteTargetField(targetField, generatedForm) ||
      !this.#canReadLinkedDisplayField(targetField, relationModule.code) ||
      !this.#filtersAreReadable(targetField, relationModule.code, generatedForm)
    ) {
      return of({ directItems: [] });
    }

    const matchingItems = this.#findAllRelationsByModuleId(relations, fullSourceEntry.id, relationModule.id);
    if (matchingItems.length === 0) return of({ directItems: [] });

    if (matchingItems.length === 1) {
      return this.#processRelationItem(matchingItems[0], mapping, relationModule.id, currentEntry, generatedForm).pipe(
        map(items => ({ directItems: items }))
      );
    }

    // Multiple matches: pre-calculate changes for ALL options
    const moduleTitle = relationModule.title ?? relationModule.singularName ?? 'Item';
    const optionResults$: Observable<RelationOptionWithItems>[] = matchingItems.map(item =>
      this.#processRelationItem(item, mapping, relationModule.id, currentEntry, generatedForm).pipe(
        map(items => ({ relationItem: item, items }))
      )
    );

    return forkJoin(optionResults$).pipe(
      map(options => {
        const viableOptions = options.filter(opt => opt.items.length > 0);

        if (viableOptions.length === 0) {
          return { directItems: [] };
        }

        if (viableOptions.length === 1) {
          return { directItems: viableOptions[0].items };
        }

        return this.#resolveMultipleRelationItems(viableOptions, moduleTitle);
      })
    );
  }

  /**
   * When multiple relation items are viable, check whether they target
   * different form controls (no conflict → auto-fill all) or the same
   * form control (conflict → show selection dialog).
   */
  #resolveMultipleRelationItems(viableOptions: RelationOptionWithItems[], moduleTitle: string): MappingProcessResult {
    const targetMap = new Map<string, { option: RelationOptionWithItems; item: AutoFillPendingItem }[]>();

    for (const option of viableOptions) {
      for (const item of option.items) {
        const path = item.result.formControlPath;
        if (!targetMap.has(path)) targetMap.set(path, []);
        targetMap.get(path)!.push({ option, item });
      }
    }

    const directItems: AutoFillPendingItem[] = [];
    const conflictEntries: { option: RelationOptionWithItems; item: AutoFillPendingItem }[] = [];

    for (const entries of targetMap.values()) {
      if (entries.length === 1) {
        directItems.push(entries[0].item);
      } else {
        conflictEntries.push(...entries);
      }
    }

    if (conflictEntries.length === 0) {
      return { directItems };
    }

    const optionMap = new Map<string, RelationOptionWithItems>();
    for (const entry of conflictEntries) {
      const id = entry.option.relationItem.entry.id;
      if (!optionMap.has(id)) {
        optionMap.set(id, { relationItem: entry.option.relationItem, items: [] });
      }
      optionMap.get(id)!.items.push(entry.item);
    }

    const conflictingOptions = Array.from(optionMap.values());

    if (conflictingOptions.length <= 1) {
      return { directItems: [...directItems, ...(conflictingOptions[0]?.items ?? [])] };
    }

    return {
      directItems,
      relationChoice: { moduleTitle, options: conflictingOptions },
    };
  }

  #processRelationItem(
    relationItem: RelationItem,
    mapping: AutoFillMapping,
    relationModuleId: string,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[]
  ): Observable<AutoFillPendingItem[]> {
    const targetField = this.#resolveTargetField(generatedForm, mapping.targetFieldKey);
    if (!targetField || !this.#canWriteTargetField(targetField, generatedForm)) return of([]);

    const leafKey = this.#extractLeafKey(mapping.targetFieldKey);
    const linkedModuleCode = mapping.sourceRelationModuleCode!;

    return this.#modulesService
      .getModuleEntry({
        moduleId: relationModuleId,
        entryId: relationItem.entry.id,
      })
      .pipe(
        map(fullLinkedEntry => {
          const linkedEntry = fullLinkedEntry as Entry;
          const results: AutoFillPendingItem[] = [];

          const isInArray = this.#isFieldInRepeatArray(generatedForm, leafKey);

          if (isInArray) {
            this.#processRepeatField(
              leafKey,
              targetField,
              linkedEntry,
              linkedModuleCode,
              currentEntry,
              generatedForm,
              results
            );
          } else {
            this.#processSimpleField(targetField, linkedEntry, linkedModuleCode, currentEntry, generatedForm, results);
          }

          return results;
        })
      );
  }

  #findAllRelationsByModuleId(relations: Relation[], sourceEntryId: string, targetModuleId: string): RelationItem[] {
    const results: RelationItem[] = [];

    for (const relation of relations) {
      if (relation.entry?.archived) continue;

      const item1 = relation.entry?.['item-1'];
      const item2 = relation.entry?.['item-2'];

      if (item1?.entry?.id === sourceEntryId && item2?.moduleId === targetModuleId) {
        results.push(item2);
      } else if (item2?.entry?.id === sourceEntryId && item1?.moduleId === targetModuleId) {
        results.push(item1);
      }
    }

    return results;
  }

  #collectResults(results: MappingProcessResult[]): CollectedResults {
    const directItems: AutoFillPendingItem[] = [];
    const relationChoices: RelationSelectionGroup[] = [];

    for (const result of results) {
      directItems.push(...result.directItems);
      if (result.relationChoice) {
        relationChoices.push(result.relationChoice);
      }
    }

    return { directItems, relationChoices };
  }

  #resolveAndConfirm(collected: CollectedResults): Observable<AutoFillResult[]> {
    const { directItems, relationChoices } = collected;

    const hasRelationChoices = relationChoices.length > 0;
    const hasOverwrites = directItems.some(item => item.needsConfirmation);

    if (!hasRelationChoices && !hasOverwrites) {
      return of(directItems.map(item => item.result));
    }

    const dialogRef = this.#dialog.open(AutoFillCombinedDialogComponent, {
      data: { relationSelections: relationChoices, directItems } as CombinedAutoFillDialogData,
      width: '480px',
    });

    return dialogRef.afterClosed().pipe(map(results => results ?? []));
  }

  #processRepeatField(
    leafKey: string,
    targetField: FormlyFieldConfig,
    linkedEntry: Entry,
    linkedModuleCode: string,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    results: AutoFillPendingItem[]
  ): void {
    const repeatPath = this.#findRepeatPathForKey(generatedForm, leafKey);
    if (!repeatPath) return;

    const arrayData = UtilityService.keyPathToValue(currentEntry, repeatPath.entryPath);
    if (!Array.isArray(arrayData)) return;

    const filters = targetField.props?.optionsArray?.[0]?.filters;

    for (let i = 0; i < arrayData.length; i++) {
      const group = arrayData[i];

      const nestedKey = repeatPath.nestedKey;
      if (nestedKey && Array.isArray(group[nestedKey])) {
        for (let j = 0; j < group[nestedKey].length; j++) {
          const innerField = group[nestedKey][j];
          const fieldValue = innerField?.[leafKey];

          if (filters?.length > 0) {
            const matches = evaluateFiltersAgainstEntry(linkedEntry, currentEntry, filters, [i, j]);
            if (!matches) continue;
          }

          const moduleValueType = this.#formService.getModuleValueTypeForTargetField(
            targetField.props.keyPath,
            linkedModuleCode,
            generatedForm
          );
          const formattedValue = this.#formService.formatEntryByModuleValueType(linkedEntry, moduleValueType);

          if (this.#valuesAreEqual(fieldValue, formattedValue)) continue;

          const entryKeyPath = `${repeatPath.entryPath}.${nestedKey}.${leafKey}`;
          const formControlPath = `${repeatPath.formControlPrefix}.${i}.${nestedKey}.${j}.${leafKey}`;
          const isEmpty =
            fieldValue == null || fieldValue === '' || (typeof fieldValue === 'object' && !fieldValue?.id);

          results.push({
            result: {
              targetFieldKeyPath: entryKeyPath,
              formControlPath,
              value: formattedValue,
              repeatIndex: [i, j],
            },
            fieldLabel: targetField.props?.label ?? leafKey,
            needsConfirmation: !isEmpty,
            oldDisplayValue: this.#extractDisplayValue(fieldValue),
            newDisplayValue: this.#extractDisplayValue(formattedValue),
          });

          if (isEmpty) return;
        }
      } else {
        const fieldValue = group?.[leafKey];

        if (filters?.length > 0) {
          const matches = evaluateFiltersAgainstEntry(linkedEntry, currentEntry, filters, [i]);
          if (!matches) continue;
        }

        const moduleValueType = this.#formService.getModuleValueTypeForTargetField(
          targetField.props.keyPath,
          linkedModuleCode,
          generatedForm
        );
        const formattedValue = this.#formService.formatEntryByModuleValueType(linkedEntry, moduleValueType);

        if (this.#valuesAreEqual(fieldValue, formattedValue)) continue;

        const entryKeyPath = `${repeatPath.entryPath}.${leafKey}`;
        const formControlPath = `${repeatPath.formControlPrefix}.${i}.${leafKey}`;
        const isEmpty = fieldValue == null || fieldValue === '' || (typeof fieldValue === 'object' && !fieldValue?.id);

        results.push({
          result: {
            targetFieldKeyPath: entryKeyPath,
            formControlPath,
            value: formattedValue,
            repeatIndex: [i],
          },
          fieldLabel: targetField.props?.label ?? leafKey,
          needsConfirmation: !isEmpty,
          oldDisplayValue: this.#extractDisplayValue(fieldValue),
          newDisplayValue: this.#extractDisplayValue(formattedValue),
        });

        if (isEmpty) return;
      }
    }
  }

  #processSimpleField(
    targetField: FormlyFieldConfig,
    linkedEntry: Entry,
    linkedModuleCode: string,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    results: AutoFillPendingItem[]
  ): void {
    const filters = targetField.props?.optionsArray?.[0]?.filters;

    if (filters?.length > 0) {
      const matches = evaluateFiltersAgainstEntry(linkedEntry, currentEntry, filters);
      if (!matches) return;
    }

    const moduleValueType = this.#formService.getModuleValueTypeForTargetField(
      targetField.props.keyPath,
      linkedModuleCode,
      generatedForm
    );
    const formattedValue = this.#formService.formatEntryByModuleValueType(linkedEntry, moduleValueType);

    const keyPath = targetField.props?.keyPath;
    const currentValue = UtilityService.keyPathToValue(currentEntry, keyPath);

    if (this.#valuesAreEqual(currentValue, formattedValue)) return;

    const isEmpty =
      currentValue == null || currentValue === '' || (typeof currentValue === 'object' && !currentValue?.id);

    const formControlPath = this.#keyPathToFormControlPath(keyPath);

    results.push({
      result: {
        targetFieldKeyPath: keyPath,
        formControlPath,
        value: formattedValue,
      },
      fieldLabel: targetField.props?.label ?? keyPath,
      needsConfirmation: !isEmpty,
      oldDisplayValue: this.#extractDisplayValue(currentValue),
      newDisplayValue: this.#extractDisplayValue(formattedValue),
    });
  }

  #processQueryMapping(
    mapping: AutoFillMapping,
    currentEntry: Entry,
    generatedForm: FormlyFieldConfig[],
    fieldArrayIndices: number[] = []
  ): Observable<MappingProcessResult> {
    const empty: MappingProcessResult = { directItems: [] };
    const queryFilters = mapping.queryFilters ?? [];

    // Every exit below is silent by design (a half-configured mapping must not break the form), so
    // log the reason — without it a mapping that never fires is undebuggable from the outside.
    const skip = (reason: string, detail?: unknown) => {
      debugLog('AutoFill:query', 'skipped:', reason, detail ?? '');
      return of(empty);
    };

    if (queryFilters.length === 0) return skip('no queryFilters configured');
    if (!mapping.queryModuleCode) return skip('no queryModuleCode configured');

    // compareField is interpolated raw into the OData $filter expression,
    // so restrict to safe dot-paths. Dashes are excluded because the
    // DistroDot OData parser (Tokenizer.cs) accepts them as Variables, but
    // Cosmos SQL then rejects `c.entry.veld-met-streep` at runtime.
    // For v1 (klant.id, artikel.id) the alphanum + dot + underscore charset
    // is sufficient; broader paths would need bracket-notation support BE-side.
    const VALID_FIELD_PATH = /^[A-Za-z0-9._]+$/;
    if (queryFilters.some(f => !VALID_FIELD_PATH.test(f.compareField)))
      return skip(
        'compareField contains a character OData rejects (e.g. a dash)',
        queryFilters.map(f => f.compareField)
      );

    // Pre-resolve dynamic context values. Hard-skip if any is null:
    // documented UX — "klant or artikel empty → price stays empty".
    // (evaluateFiltersAgainstEntry returns true for null context, which is
    // the opposite semantic — hence the explicit pre-check.)
    // Scoped by the trigger's repeat position, so a filter reading a sibling field inside the same
    // fieldArray row gets that row's value instead of an array of every row's.
    const contextValues = queryFilters.map(f => resolveScopedKeyPath(currentEntry, f.fieldKeyPath, fieldArrayIndices));
    if (contextValues.some(v => v == null))
      return skip('a filter value is absent from formState.entry', {
        paths: queryFilters.map(f => f.fieldKeyPath),
        values: contextValues,
      });

    // Render each context value as an OData literal. `#odataLiteral` returns
    // null for un-renderable values (NaN/Infinity, single-quote-containing
    // strings that the BE tokenizer cannot escape); skip the whole query
    // rather than emit a malformed $filter.
    const literals = contextValues.map(v => this.#odataLiteral(v));
    if (literals.some(l => l === null))
      return skip('filter value cannot be rendered as an OData literal', contextValues);

    const targetModule = this.#modulesStore.getModuleByCode(mapping.queryModuleCode);
    if (!targetModule) return skip('queryModuleCode unknown in the store', mapping.queryModuleCode);

    const targetField = this.#resolveTargetField(generatedForm, mapping.targetFieldKey);
    if (!targetField) return skip('target field not found in generatedForm', mapping.targetFieldKey);
    if (!this.#canWriteTargetField(targetField, generatedForm)) return skip('target field is not writable');
    if (!this.#queryFieldsAreReadable(mapping, targetModule.id, targetField, generatedForm)) {
      return skip('query source or result field is not readable');
    }
    const keyPath = targetField.props?.keyPath ?? mapping.targetFieldKey;

    // A target inside a fieldArray belongs to one row. The trigger supplies that position; without
    // it the result would carry an index-less path, which writes through to every row.
    const leafKey = this.#extractLeafKey(mapping.targetFieldKey);
    const inRepeat = this.#isFieldInRepeatArray(generatedForm, leafKey);
    const repeatPath = inRepeat ? this.#findRepeatPathForKey(generatedForm, leafKey) : null;
    if (inRepeat && !repeatPath) return skip('target field is in a repeat whose path is unresolvable', leafKey);

    const repeatDepth = repeatPath ? (repeatPath.nestedKey ? 2 : 1) : 0;
    if (repeatPath && fieldArrayIndices.length < repeatDepth)
      return skip('target field is in a repeat but the trigger carries no row index', {
        targetFieldKey: mapping.targetFieldKey,
        fieldArrayIndices,
      });

    const repeatIndex = repeatPath ? fieldArrayIndices.slice(0, repeatDepth) : undefined;
    const formControlPath = repeatPath
      ? [
          repeatPath.formControlPrefix,
          repeatIndex[0],
          ...(repeatPath.nestedKey ? [repeatPath.nestedKey, repeatIndex[1]] : []),
          leafKey,
        ].join('.')
      : this.#keyPathToFormControlPath(keyPath);

    // Server-side narrowing: emit candidates that match at least one filter
    // (mirrors `score > 0` semantics). Most-specific scoring happens
    // client-side because OData has no native match-count expression.
    // $orderBy=createdAt desc → tiebreak on ties is the most-recent entry.
    const $filter = queryFilters.map((f, i) => `${f.compareField} eq ${literals[i]}`).join(' or ');

    return this.#modulesService
      .getModuleEntries({
        moduleId: targetModule.id,
        $filter,
        $orderBy: 'createdAt desc',
      })
      .pipe(
        map(rawEntries => {
          // API returns GenericEntry[] (auto-generated shape); service-internal
          // convention casts to Entry — see :79, :152 for precedent.
          const candidates = rawEntries as unknown as Entry[];

          // Score via the shared filter-evaluator (id-extraction, operator
          // dispatch — same engine used by the relation pad above).
          let best: { entry: Entry; score: number } | null = null;
          for (const candidate of candidates) {
            const score = queryFilters.reduce(
              (acc, f) => acc + (evaluateFiltersAgainstEntry(candidate, currentEntry, [f], fieldArrayIndices) ? 1 : 0),
              0
            );
            if (score > 0 && (!best || score > best.score)) {
              best = { entry: candidate, score };
            }
          }
          if (!best) return empty;

          // With a result path the mapping copies a single value out of the match (a price, a
          // rate). Without one the match itself is the value, formatted the way the target field
          // stores a linked entry — that is what fills a module-backed select or autocomplete.
          const value = mapping.queryResultPath
            ? UtilityService.keyPathToValue(best.entry, mapping.queryResultPath)
            : this.#formService.formatEntryByModuleValueType(
                best.entry,
                this.#formService.getModuleValueTypeForTargetField(keyPath, mapping.queryModuleCode!, generatedForm)
              );
          if (value == null) return empty;

          const currentValue = resolveScopedKeyPath(currentEntry, keyPath, repeatIndex ?? []);
          if (this.#valuesAreEqual(currentValue, value)) return empty;

          const isEmpty =
            currentValue == null ||
            currentValue === '' ||
            (typeof currentValue === 'object' && !(currentValue as { id?: string })?.id);

          return {
            directItems: [
              {
                result: {
                  targetFieldKeyPath: keyPath,
                  formControlPath,
                  value,
                  ...(repeatIndex ? { repeatIndex } : {}),
                },
                fieldLabel: targetField.props?.label ?? keyPath,
                needsConfirmation: !isEmpty,
                oldDisplayValue: this.#extractDisplayValue(currentValue),
                newDisplayValue: this.#extractDisplayValue(value),
              },
            ],
          };
        })
      );
  }

  #odataLiteral(value: unknown): string | null {
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
    if (typeof value === 'boolean') return String(value);
    const s = String(value);
    // The DistroDot OData tokenizer (Tokenizer.cs:76-89) stops at the first
    // closing `'` and supports NO escape sequence — neither `''` (OData v4
    // standard) nor `\'` works. Returning null forces the caller to skip
    // rather than emit a $filter that misparses.
    if (s.includes("'")) return null;
    return `'${s}'`;
  }

  #conditionsAreReadable(mapping: AutoFillMapping, generatedForm: FormlyFieldConfig[]): boolean {
    return (mapping.conditions ?? []).every(condition => this.#canReadFormField(generatedForm, condition.compareField));
  }

  #filtersAreReadable(
    targetField: FormlyFieldConfig,
    linkedModuleCode: string,
    generatedForm: FormlyFieldConfig[]
  ): boolean {
    const linkedModule = this.#modulesStore.getModuleByCode(linkedModuleCode);
    const linkedForm = linkedModule ? this.#modulesStore.generatedForms()[linkedModule.id] : undefined;
    return (targetField.props?.optionsArray?.[0]?.filters ?? []).every(
      filter =>
        this.#canReadFormField(linkedForm, filter.compareField) &&
        (filter.basedOn === 'static' || this.#canReadFormField(generatedForm, filter.fieldKeyPath))
    );
  }

  #queryFieldsAreReadable(
    mapping: AutoFillMapping,
    queryModuleId: string,
    targetField: FormlyFieldConfig,
    generatedForm: FormlyFieldConfig[]
  ): boolean {
    const queryForm = this.#modulesStore.generatedForms()[queryModuleId];
    const filtersReadable = (mapping.queryFilters ?? []).every(
      filter =>
        this.#canReadFormField(queryForm, filter.compareField) &&
        (filter.basedOn === 'static' || this.#canReadFormField(generatedForm, filter.fieldKeyPath))
    );
    if (!filtersReadable) return false;
    if (mapping.queryResultPath) return this.#canReadFormField(queryForm, mapping.queryResultPath);

    const moduleCode = targetField.props?.optionsArray?.[0]?.moduleCode ?? mapping.queryModuleCode;
    return !!moduleCode && this.#canReadLinkedDisplayField(targetField, moduleCode);
  }

  #canWriteTargetField(targetField: FormlyFieldConfig, generatedForm: FormlyFieldConfig[]): boolean {
    return canWriteFieldRight(
      effectiveFieldRightForKeyPath(
        generatedForm,
        targetField.props?.keyPath ?? String(targetField.key ?? ''),
        this.#userStore.currentUserRoles()
      )
    );
  }

  #canReadLinkedDisplayField(targetField: FormlyFieldConfig, moduleCode: string): boolean {
    const module = this.#modulesStore.getModuleByCode(moduleCode);
    if (!module) return false;
    const option = targetField.props?.optionsArray?.find(item => item.moduleCode === moduleCode);
    const displayKeyPath = option?.moduleLinkedField ?? module.titelKeyPath;
    return this.#canReadFormField(this.#modulesStore.generatedForms()[module.id], displayKeyPath);
  }

  #canReadModuleField(entry: Entry, keyPath: string, moduleCode: string): boolean {
    const moduleId = entry.moduleId ?? this.#modulesStore.getModuleByCode(moduleCode)?.id;
    return this.#canReadFormField(moduleId ? this.#modulesStore.generatedForms()[moduleId] : undefined, keyPath);
  }

  #canReadFormField(generatedForm: FormlyFieldConfig[] | undefined, keyPathOrKey: string | undefined): boolean {
    if (!generatedForm?.length || !keyPathOrKey) return false;
    let field = this.#resolveTargetField(generatedForm, keyPathOrKey);
    let candidatePath = keyPathOrKey;
    while (!field && candidatePath.includes('.')) {
      candidatePath = candidatePath.slice(0, candidatePath.lastIndexOf('.'));
      field = FormHelper.getFieldByKeyPath(candidatePath, generatedForm);
    }
    if (!field) return false;
    const keyPath = field.props?.keyPath ?? String(field.key ?? '');
    return canReadFieldRight(effectiveFieldRightForKeyPath(generatedForm, keyPath, this.#userStore.currentUserRoles()));
  }

  #resolveTargetField(generatedForm: FormlyFieldConfig[], targetFieldKey: string): FormlyFieldConfig | undefined {
    if (targetFieldKey.includes('.')) {
      const byKeyPath = FormHelper.getFieldByKeyPath(targetFieldKey, generatedForm);
      if (byKeyPath) return byKeyPath;
    }

    return this.#findFieldByKey(generatedForm, targetFieldKey);
  }

  #extractLeafKey(targetFieldKey: string): string {
    if (!targetFieldKey.includes('.')) return targetFieldKey;
    const parts = targetFieldKey.split('.');
    return parts[parts.length - 1];
  }

  #findFieldByKey(fields: FormlyFieldConfig[], key: string): FormlyFieldConfig | undefined {
    // Key-only contract (no keyPath matching) — delegated to the canonical walker.
    return FormHelper.findFieldByKey(fields, key, { matchKeyPaths: false }) ?? undefined;
  }

  #isFieldInRepeatArray(fields: FormlyFieldConfig[], key: string, insideArray = false): boolean {
    for (const field of fields) {
      if (field.key === key) return insideArray;
      if (field.fieldGroup) {
        const found = this.#isFieldInRepeatArray(field.fieldGroup, key, insideArray);
        if (found) return true;
      }
      const fieldArray = typeof field.fieldArray === 'function' ? undefined : field.fieldArray;
      if (fieldArray?.fieldGroup) {
        const found = this.#isFieldInRepeatArray(fieldArray.fieldGroup, key, true);
        if (found) return true;
      }
    }
    return false;
  }

  #findRepeatPathForKey(
    fields: FormlyFieldConfig[],
    targetKey: string,
    currentEntryPath = 'entry.general',
    currentFormPath = ''
  ): { entryPath: string; formControlPrefix: string; nestedKey?: string } | null {
    for (const field of fields) {
      if (field.key === targetKey) return null;

      if (field.fieldGroup) {
        const result = this.#findRepeatPathForKey(field.fieldGroup, targetKey, currentEntryPath, currentFormPath);
        if (result) return result;
      }

      const fieldArray = typeof field.fieldArray === 'function' ? undefined : field.fieldArray;
      if (fieldArray?.fieldGroup && field.key) {
        const arrayKey = String(field.key);
        const newEntryPath = `${currentEntryPath}.${arrayKey}`;
        const newFormPath = currentFormPath ? `${currentFormPath}.${arrayKey}` : arrayKey;

        const directChild = fieldArray.fieldGroup.find(f => f.key === targetKey);
        if (directChild) {
          return { entryPath: newEntryPath, formControlPrefix: newFormPath };
        }

        for (const innerField of fieldArray.fieldGroup) {
          const innerFieldArray = typeof innerField.fieldArray === 'function' ? undefined : innerField.fieldArray;
          if (innerFieldArray?.fieldGroup && innerField.key) {
            const nestedKey = String(innerField.key);
            const nestedChild = innerFieldArray.fieldGroup.find(f => f.key === targetKey);
            if (nestedChild) {
              return {
                entryPath: newEntryPath,
                formControlPrefix: newFormPath,
                nestedKey,
              };
            }
          }
        }

        const result = this.#findRepeatPathForKey(fieldArray.fieldGroup, targetKey, newEntryPath, newFormPath);
        if (result) return result;
      }
    }
    return null;
  }

  #valuesAreEqual(oldValue: unknown, newValue: unknown): boolean {
    if ((oldValue == null || oldValue === '') && (newValue == null || newValue === '')) {
      return true;
    }
    if (
      typeof oldValue === 'object' &&
      (oldValue as Record<string, unknown>)?.id &&
      typeof newValue === 'object' &&
      (newValue as Record<string, unknown>)?.id
    ) {
      return (oldValue as Record<string, unknown>).id === (newValue as Record<string, unknown>).id;
    }
    return oldValue === newValue;
  }

  #extractDisplayValue(value: unknown): string {
    if (value == null || value === '') return '(leeg)';
    if (typeof value === 'object' && (value as Record<string, unknown>)?.selectedInput) {
      return String((value as Record<string, unknown>).selectedInput);
    }
    return String(value);
  }

  #keyPathToFormControlPath(keyPath: string): string {
    if (!keyPath) return '';
    return keyPath.replace(/^entry\.general\./, '').replace(/^entry\./, '');
  }
}
